/**
 * WizardStepReview — Step 3 of the New Transformation wizard.
 * Read-only summary of all selected sources and their column selections.
 */
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AttachFile,
  Cable,
} from '@mui/icons-material';
import type { ConnectorSourceEntry } from './ConnectorSourcePicker';
import { fileKey, connKey } from './WizardStepConfigure';

interface WizardStepReviewProps {
  files: File[];
  connectorSources: ConnectorSourceEntry[];
  columnSelections: Map<string, string[]>;
}

const WizardStepReview: React.FC<WizardStepReviewProps> = ({
  files,
  connectorSources,
  columnSelections,
}) => {
  // Group connector sources by connector
  const connectorGroups = connectorSources.reduce<Record<string, ConnectorSourceEntry[]>>(
    (acc, entry) => {
      const groupKey = entry.connectorId;
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(entry);
      return acc;
    },
    {},
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Review your selections before starting the transformation
      </Typography>

      <Paper variant="outlined">
        <List disablePadding>
          {/* File sources */}
          {files.map((file, index) => {
            const key = fileKey(file);
            // Check for multi-sheet entries (key::SheetName)
            const sheetKeys = Array.from(columnSelections.keys()).filter(
              (k) => k.startsWith(`${key}::`)
            );
            const activeSheetKeys = sheetKeys.filter((k) => {
              const selected = columnSelections.get(k);
              return !(selected && selected.length === 0);
            });

            if (sheetKeys.length > 0) {
              if (activeSheetKeys.length === 0) return null;
              // Multi-sheet Excel — render per-sheet summaries
              return (
                <React.Fragment key={key}>
                  <ListItem
                    sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, width: '100%' }}>
                      <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 28, height: 28 }}>
                        <AttachFile sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>
                        {file.name}
                      </Typography>
                      <Chip
                        label={`${activeSheetKeys.length} sheet${activeSheetKeys.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                    </Stack>
                    <Box sx={{ pl: 5, width: '100%' }}>
                      {activeSheetKeys.map((sheetKey) => {
                        const sheetName = sheetKey.split('::').pop() || '';
                        const selected = columnSelections.get(sheetKey);
                        return (
                          <Box key={sheetKey} sx={{ mb: 0.75 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {sheetName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                &mdash;
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {selected ? `${selected.length} column${selected.length !== 1 ? 's' : ''}` : 'all columns'}
                              </Typography>
                            </Stack>
                            {selected && selected.length > 0 && (
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                {selected.slice(0, 6).join(', ')}
                                {selected.length > 6 && ` +${selected.length - 6} more`}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </ListItem>
                  {(index < files.length - 1 || Object.keys(connectorGroups).length > 0) && (
                    <Divider />
                  )}
                </React.Fragment>
              );
            }

            // Single-sheet file
            const selected = columnSelections.get(key);
            if (selected && selected.length === 0) return null;

            return (
              <React.Fragment key={key}>
                <ListItem sx={{ py: 1.5 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 28, height: 28 }}>
                      <AttachFile sx={{ fontSize: 16 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {file.name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {selected ? (
                          <>
                            <Chip
                              label={`${selected.length} column${selected.length !== 1 ? 's' : ''} selected`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: 10, height: 20, mr: 1, mb: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {selected.slice(0, 8).join(', ')}
                              {selected.length > 8 && ` +${selected.length - 8} more`}
                            </Typography>
                          </>
                        ) : (
                          <Chip
                            label="all columns"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10, height: 20 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {(index < files.length - 1 || Object.keys(connectorGroups).length > 0) && (
                  <Divider />
                )}
              </React.Fragment>
            );
          })}

          {/* Connector sources grouped by connector */}
          {Object.entries(connectorGroups).map(([connectorId, entries], groupIndex) => {
            const activeEntries = entries.filter((entry) => {
              const key = connKey(entry);
              const selected = columnSelections.get(key) || entry.selectedColumns;
              return !(selected && selected.length === 0);
            });
            if (activeEntries.length === 0) return null;

            const first = activeEntries[0];
            return (
              <React.Fragment key={connectorId}>
                <ListItem
                  sx={{
                    py: 1.5,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Connector header */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, width: '100%' }}>
                    <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark', width: 28, height: 28 }}>
                      <Cable sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                      {first.connectorName}
                    </Typography>
                    <Chip
                      label={first.connectorType}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 9, height: 18 }}
                    />
                  </Stack>

                  {/* Tables in this connector */}
                  <Box sx={{ pl: 5, width: '100%' }}>
                    {activeEntries.map((entry) => {
                      const key = connKey(entry);
                      const selected = columnSelections.get(key) || entry.selectedColumns;
                      return (
                        <Box key={entry.tableName} sx={{ mb: 0.75 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontSize: 12 }}
                            >
                              {entry.tableName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              &mdash;
                            </Typography>
                            {selected && selected.length > 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {selected.length} column{selected.length !== 1 ? 's' : ''}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                all columns
                              </Typography>
                            )}
                          </Stack>
                          {selected && selected.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ display: 'block', pl: 0 }}
                            >
                              {selected.slice(0, 6).join(', ')}
                              {selected.length > 6 && ` +${selected.length - 6} more`}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </ListItem>
                {groupIndex < Object.keys(connectorGroups).length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

export default WizardStepReview;
